"use client";

import React, {useState, useRef, useEffect} from "react";

import Chip from "@/components/global/ui/Chip";
import DecorationLine from "@/components/global/ui/DecorationLine";
import {cn} from "@/lib/utils";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";
import BackgroundVideo from "@/components/global/utils/BackgroundVideo";
import DecorationSquares from "@/components/global/ui/DecorationSquares";
import ScrollingBanner from "@/components/global/ui/ScrollingBanner";
import InfoBox from "@/components/global/ui/InfoBox";
import CornerList from "@/components/global/ui/CornerList";

import {PageHomeNewSchema} from "@/sanity/pages/PageHomeNewTemplate";

import component1 from "@/public/home-new/component-1-fallback.png";
import component2 from "@/public/home-new/component-2-fallback.png";
import component3 from "@/public/home-new/component-3-fallback.png";
import component4 from "@/public/home-new/component-4-fallback.png";
import {CTACornerButton} from "@/shadcn/ui/corner-button";

const componentAssets = [
  { playbackID: "P6X95gsZYVmS7Dp9ljaCeFB2I02GTBiWKj5wBYLqf5MY", fallbackImage: component1 },
  { playbackID: "NDa9S3KQdXU49OOKuUP02x5c00jn02fNBdolq9BZ74yGAw", fallbackImage: component2 },
  { playbackID: "OSBosbIRuobW4rARKRrkxlgl01ghgoxq5OAmBj8vEsck", fallbackImage: component3 },
  { playbackID: "gLfal802695sHQfKsEeW00I01q1luCrZYnHVyaXohIV2ZQ", fallbackImage: component4 },
]

export default function ComponentsSection({
  data,
  className
}: {
  data: PageHomeNewSchema;
  className?: string;
}) {
  const chipRefs = useRef<(HTMLDivElement | null)[]>([]);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const [chipPositions, setChipPositions] = useState<number[]>([]);
  const [maxCardHeight, setMaxCardHeight] = useState<number | null>(null);
  const [headerHeight, setHeaderHeight] = useState<number | null>(null);
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  const [hasEnoughVerticalSpace, setHasEnoughVerticalSpace] = useState(true);

  useEffect(() => {
    const calculatePositions = () => {
      if (!chipRefs.current.length) return;

      const allChipsMeasured = chipRefs.current.every(chip => chip && chip.offsetWidth > 0);
      if (!allChipsMeasured) return;

      const positions: number[] = [];
      let cumulativeLeft = 0;

      const remInPixels = parseFloat(getComputedStyle(document.documentElement).fontSize);

      chipRefs.current.forEach((chip) => {
        positions.push(cumulativeLeft);
        if (chip) {
          const widthInPixels = chip.offsetWidth;
          const widthInRem = widthInPixels / remInPixels;
          cumulativeLeft += widthInRem + 1;
        }
      });

      setChipPositions(positions);
    };

    const timeout1 = setTimeout(calculatePositions, 100);
    const timeout2 = setTimeout(calculatePositions, 300);
    const timeout3 = setTimeout(calculatePositions, 500);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(calculatePositions);
    }

    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(calculatePositions, 100);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
    };
  }, [data?.componentsItems]);

  useEffect(() => {
    const calculateMaxHeight = () => {
      if (!cardRefs.current.length || maxCardHeight !== null) return;

      let maxHeightInPixels = 0;

      cardRefs.current.forEach((card) => {
        if (card) {
          const height = card.offsetHeight;
          if (height > maxHeightInPixels) {
            maxHeightInPixels = height;
          }
        }
      });

      if (maxHeightInPixels > 0) {
        const remInPixels = parseFloat(getComputedStyle(document.documentElement).fontSize);
        const maxHeightInRem = maxHeightInPixels / remInPixels;
        setMaxCardHeight(maxHeightInRem);
      }
    };

    const timeout1 = setTimeout(calculateMaxHeight, 150);
    const timeout2 = setTimeout(calculateMaxHeight, 400);
    const timeout3 = setTimeout(calculateMaxHeight, 800);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        setTimeout(calculateMaxHeight, 100);
      });
    }

    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(calculateMaxHeight, 200);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
    };
  }, [data?.componentsItems, maxCardHeight]);

  // calculate header height for sticky offset of components
  useEffect(() => {
    const calculateHeaderHeight = () => {
      if (!headerRef.current) return;

      const heightInPixels = headerRef.current.offsetHeight;
      if (heightInPixels > 0) {
        const remInPixels = parseFloat(getComputedStyle(document.documentElement).fontSize);
        const heightInRem = heightInPixels / remInPixels;
        setHeaderHeight(heightInRem);
      }
    };

    const timeout1 = setTimeout(calculateHeaderHeight, 150);
    const timeout2 = setTimeout(calculateHeaderHeight, 400);
    const timeout3 = setTimeout(calculateHeaderHeight, 800);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        setTimeout(calculateHeaderHeight, 100);
      });
    }

    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(calculateHeaderHeight, 200);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
    };
  }, [headerHeight]);

  useEffect(() => {
    const handleResize = () => {
      const isLg = window.innerWidth >= 993;
      setIsLargeScreen(isLg);
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Check if there's enough vertical space for sticky header + cards
  useEffect(() => {
    const checkVerticalSpace = () => {
      if (!isLargeScreen || headerHeight === null || maxCardHeight === null) {
        setHasEnoughVerticalSpace(true);
        return;
      }

      const remInPixels = parseFloat(getComputedStyle(document.documentElement).fontSize);
      const headerOffsetRem = 3;
      const totalRequiredHeight = (headerOffsetRem + headerHeight + maxCardHeight) * remInPixels;
      const totalWithBuffer = totalRequiredHeight + remInPixels;

      const hasSpace = window.innerHeight >= totalWithBuffer;
      setHasEnoughVerticalSpace(hasSpace);
    };

    checkVerticalSpace();

    window.addEventListener('resize', checkVerticalSpace);

    return () => {
      window.removeEventListener('resize', checkVerticalSpace);
    };
  }, [headerHeight, maxCardHeight, isLargeScreen]);

  return (
    <section className={cn(
      "relative overflow-clip",
      className
    )}>
      <div className="relative overflow-clip bg-[#0A1A1D] pt-[13rem] lg:pt-[20rem] pb-[13rem] lg:pb-[20rem] flex flex-col items-center">
        <DecorationLine
          className="top-0"
          orientation="vertical"
          position="left"
          style="light"
        />
        <DecorationLine
          className="top-0"
          orientation="vertical"
          position="right"
          style="light"
        />
        <DecorationSquares
          className="top-[2px]"
          style="green"
        />

        <div
          ref={headerRef}
          className={cn(
            "container relative flex flex-col items-center pb-[6rem] lg:pb-[8rem]",
            hasEnoughVerticalSpace && "lg:sticky top-[3rem]"
          )}
        >
          <Chip
            image={data?.componentsChip?.icon}
            text={data?.componentsChip?.text}
          />

          {data?.componentsTitle && (
            <div className="richtext w-[min(70rem,100%)] prose-headings:text-white prose-p:text-white/70">
              <PortableText
                value={data.componentsTitle}
                components={richTextComponent}
              />
            </div>
          )}

          {data?.componentsCTA && (
            <CTACornerButton
              className="mt-[3rem]"
              data={data.componentsCTA}
            />
          )}
        </div>

        <div className="container flex flex-col items-center">
          {data?.componentsItems && (
            <div className="flex flex-col gap-[10rem] lg:gap-[40rem] w-[min(164rem,100%)] relative">
              {data?.componentsItems.map((item, index) => (
                <div
                  key={index}
                  className="lg:sticky"
                  style={{
                    ...(isLargeScreen && {
                      top: hasEnoughVerticalSpace && headerHeight !== null
                        ? `${headerHeight + 3}rem`
                        : '10rem'
                    })
                  }}
                >
                  <div
                    ref={el => {
                      (cardRefs.current[index] = el);
                    }}
                    className="flex flex-col lg:flex-row-reverse items-center lg:justify-between gap-[3rem] relative bg-[#112628] p-[2.5rem] sm:p-[4rem] md:p-[8rem] lg:py-[9rem] lg:pl-[8rem] lg:pr-[5rem]"
                    style={{
                      borderTop: `3px solid ${item?.color}`,
                      ...(maxCardHeight !== null && isLargeScreen && {
                        height: `${maxCardHeight}rem`
                      })
                    }}
                  >
                    <div
                      ref={el => {
                        (chipRefs.current[index] = el);
                      }}
                      className={cn(
                        "inline-flex items-center gap-[1rem] min-h-[5rem] px-[2rem] prose-p:text-black",
                        "[clip-path:polygon(0_0,calc(100%-1.875rem)_0,100%_1.875rem,100%_100%,0_100%)]",
                        "absolute bottom-full -left-[1px]",
                      )}
                      style={{
                        backgroundColor: `${item?.color}`,
                        ...(chipPositions[index] !== undefined && isLargeScreen && {
                          left: `${chipPositions[index]}rem`
                        })
                      }}
                    >
                      <p className="font-geistmono mb-0 leading-none text-[2rem]">KOMPONENTE {index + 1}</p>
                    </div>

                    <div className={cn(
                      "md:w-[min(60rem,100%)] flex-shrink-0 relative h-full aspect-square flex flex-col justify-center mix-blend-screen opacity-70 overflow-clip",
                      index === 0 ? "w-[40rem]" : "w-[32rem]"
                    )}>
                      <div className={cn(
                        "absolute w-full aspect-square",
                        index === 0 ? "lg:scale-[1.125] lg:origin-right" : ""
                      )}>
                        <BackgroundVideo
                          playbackId={componentAssets[index % componentAssets.length].playbackID}
                          fallbackImage={componentAssets[index % componentAssets.length].fallbackImage}
                        />
                      </div>
                    </div>

                    <div className="h-full w-[min(80.125rem,100%)] flex flex-col lg:justify-between gap-[2rem] xl:gap-[5rem]">
                      <div className="flex flex-col items-start gap-[2rem]">
                        {item?.title && (
                          <div className="richtext richtext-start prose-headings:text-[3rem] sm:prose-headings:text-[4rem] lg:prose-headings:text-[5rem] prose-headings:text-white prose-headings:font-medium prose-headings:font-geist prose-p:text-white/60">
                            <PortableText
                              value={item.title}
                              components={richTextComponent}
                            />
                          </div>
                        )}

                        <hr className="border-t border-white/10 w-full" />
                      </div>

                      <div className="flex flex-col items-start">
                        {item?.listTitle && (
                          <p className="mb-[2rem] font-geistmono text-white/50 uppercase text-[2rem]">{item.listTitle}</p>
                        )}

                        <CornerList
                          items={item?.list}
                          color={item?.color}
                        />

                        {item?.outcome?.title && item?.outcome?.text && (
                          <InfoBox
                            className="mt-[3rem]"
                            title={item.outcome.title}
                            text={item.outcome.text}
                            color={item.color}
                          />
                        )}

                        {item?.cta && (
                          <CTACornerButton
                            style={{background: `${item.color} !important`}}
                            className="mt-[2rem]"
                            data={item.cta}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="relative overflow-clip">
        {data?.componentsScroll &&
          <ScrollingBanner
            text={data.componentsScroll}
          />
        }

        <DecorationLine
          className="bottom-0 left-0"
          orientation="horizontal"
        />
      </div>
    </section>
  );
}
